import axios from 'axios';
import { backendApiUrl } from '../../config/backendApiUrl';
import { API_ROUTES } from '../../shared/apiRoutes/apiRoutes';
import { getAuth } from 'firebase/auth';
import { v4 as uuidv4 } from 'uuid';

// Determine if we're in development or production
const isDevelopment = import.meta.env.DEV;

// Always use relative path so Firebase Hosting rewrites handle routing.
// The backend will be reached via the Firebase rewrite from / → Cloud Run.
// NOTE: API_ROUTES constants already include the '/api' prefix, so we use an empty base URL
const API_BASE_URL = '';

// Create axios instance with base URL and common headers
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 seconds timeout
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use((config) => {
  const requestUrl = String(config.url || '');
  if (requestUrl.startsWith('/api/') || requestUrl === '/api') {
    config.url = backendApiUrl(requestUrl);
    config.baseURL = '';
  }
  return config;
});

// Helper: ensure CSRF token exists in localStorage by fetching it if missing
const ensureCsrfTokenForAxios = async () => {
  try {
    let token = null;
    try {
      token = typeof localStorage !== 'undefined' ? localStorage.getItem('csrfToken') : null;
    } catch (_) {}
    if (token) return token;

    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) return null;

    const idToken = await user.getIdToken();
    const res = await fetch(backendApiUrl('/api/v2/admin/agents/csrf-token'), {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${idToken}`,
        Accept: 'application/json',
      },
      credentials: 'include',
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json?.data?.csrfToken) {
      token = json.data.csrfToken;
      try {
        if (typeof localStorage !== 'undefined') {
          localStorage.setItem('csrfToken', token);
        }
      } catch (_) {}
      return token;
    }
    console.warn('[agentService] Failed to prefetch CSRF token', json);
    return null;
  } catch (e) {
    console.warn('[agentService] Error ensuring CSRF token', e);
    return null;
  }
};

// Request interceptor to add auth token and required headers
api.interceptors.request.use(
  async config => {
    console.log('[API Request]', {
      method: config.method?.toUpperCase(),
      url: `${config.baseURL || ''}${config.url}`,
      headers: config.headers,
      data: config.data,
    });
    const auth = getAuth();
    const user = auth.currentUser;

    if (user) {
      const token = await user.getIdToken();
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    // For non-GET requests, add CSRF token; prefetch if missing
    if (config.method !== 'get' && config.method !== 'GET') {
      let csrfToken = null;
      try {
        csrfToken = typeof localStorage !== 'undefined' ? localStorage.getItem('csrfToken') : null;
      } catch (_) {}
      if (!csrfToken) {
        csrfToken = await ensureCsrfTokenForAxios();
      }
      if (csrfToken) {
        config.headers['X-CSRF-TOKEN'] = csrfToken;
      }
    }

    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  response => {
    return response.data;
  },
  error => {
    console.error('[API Response Error]', {
      message: error.message,
      isAxiosError: error.isAxiosError,
      config: {
        method: error.config?.method?.toUpperCase(),
        url: `${error.config?.baseURL || ''}${error.config?.url}`,
        headers: error.config?.headers,
        data: error.config?.data,
      },
      request: error.request
        ? {
            readyState: error.request.readyState,
            status: error.request.status,
            statusText: error.request.statusText,
          }
        : 'No request object',
      response: error.response
        ? {
            status: error.response.status,
            statusText: error.response.statusText,
            headers: error.response.headers,
            data: error.response.data,
          }
        : 'No response object',
      toJSON: error.toJSON ? error.toJSON() : 'No toJSON method',
    });

    // Handle errors
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      const errorData = error.response.data || {};
      const errorMessage = errorData.error || 'An error occurred';
      const errorCode = errorData.error_code || 'UNKNOWN_ERROR';

      // Create a custom error with more details
      const customError = new Error(errorMessage);
      customError.code = errorCode;
      customError.status = error.response.status;
      customError.details = errorData.details;
      customError.validationErrors = errorData.validation_errors;

      // CRITICAL: Preserve the response object for fallback logic to detect 404s
      customError.response = {
        status: error.response.status,
        data: error.response.data,
      };
      customError.requestId = error.response.headers?.['x-request-id'];

      // Log the error we're about to throw for debugging
      console.log(`Throwing error with status ${customError.response?.status}:`, customError);
      return Promise.reject(customError);
    } else if (error.request) {
      // The request was made but no response was received
      return Promise.reject(new Error('No response from server. Please check your connection.'));
    } else {
      // Something happened in setting up the request that triggered an Error
      return Promise.reject(error);
    }
  }
);

/**
 * Fetch a list of agents with optional filtering and pagination
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.limit - Items per page (default: 20, max: 100)
 * @param {string} params.status - Filter by status (idle, busy, error, offline, rebooting)
 * @param {string} params.tags - Comma-separated list of tags to filter by
 * @param {string} params.search - Search query string
 * @returns {Promise<Object>} - Agent list and pagination info
 */
export const listAgents = async (params = {}) => {
  const { page = 1, limit = 20, ...filters } = params;

  // Prepare query parameters
  const queryParams = new URLSearchParams({
    page: page.toString(),
    limit: Math.min(limit, 100).toString(), // Enforce max limit
    ...Object.fromEntries(
      Object.entries(filters)
        .filter(([_, value]) => value !== null && value !== undefined && value !== '')
        .map(([key, value]) => [key, String(value)])
    ),
  });

  let response;
  let usedLegacyEndpoint = false;

  try {
    // Try v2 endpoint first
    console.log(`Attempting to fetch agents from v2 endpoint: ${API_ROUTES.AGENTS_LIST}`);
    response = await api.get(`${API_ROUTES.AGENTS_LIST}?${queryParams}`);
    console.log('Successfully fetched agents from v2 endpoint');
  } catch (error) {
    console.error(`Error fetching agents from v2 endpoint ${API_ROUTES.AGENTS_LIST}:`, error);

    // Check if it's a 503 Service Unavailable or other server error
    if (
      error.response &&
      (error.response.status === 503 ||
        error.response.status === 501 ||
        error.response.status === 404)
    ) {
      console.log(
        `V2 endpoint unavailable (${error.response.status}), falling back to legacy endpoint: ${API_ROUTES.AGENTS_LIST_LEGACY}`
      );

      try {
        // Fall back to legacy endpoint
        response = await api.get(`${API_ROUTES.AGENTS_LIST_LEGACY}?${queryParams}`);
        console.log('Successfully fetched agents from legacy endpoint');
        usedLegacyEndpoint = true;
      } catch (legacyError) {
        console.error(
          `Error fetching agents from legacy endpoint ${API_ROUTES.AGENTS_LIST_LEGACY}:`,
          legacyError
        );
        throw legacyError;
      }
    } else {
      // For other errors, just throw
      throw error;
    }
  }

  // Extract agents array correctly based on actual response structure
  let agents = [];
  if (Array.isArray(response)) {
    agents = response;
  } else if (response && Array.isArray(response.data)) {
    agents = response.data;
  } else if (response && response.data && Array.isArray(response.data.agents)) {
    agents = response.data.agents;
  } else if (response && response.data && Array.isArray(response.data.items)) {
    // Handle agents in data.items (as seen in the latest API response)
    agents = response.data.items;
  } else {
    console.warn('Unable to find agents array in response:', response);
  }

  return {
    success: true,
    data: {
      agents: agents,
      pagination: response.data?.pagination || {
        total: agents.length,
        page: parseInt(page, 10) || 1,
        limit: Math.min(limit, 100),
      },
    },
    requestId: response.headers?.['x-request-id'],
    usedLegacyEndpoint: usedLegacyEndpoint,
  };
};

/**
 * Get details of a specific agent
 * @param {string} agentId - ID of the agent to retrieve
 * @returns {Promise<Object>} - Agent details
 */
export const getAgentDetails = async agentId => {
  let response;
  let usedLegacyEndpoint = false;

  try {
    // Try v2 endpoint first
    const url = API_ROUTES.AGENT_DETAILS.replace(':agentId', agentId);
    console.log(`Attempting to fetch agent details from v2 endpoint: ${url}`);
    response = await api.get(url);
    console.log('Successfully fetched agent details from v2 endpoint');
  } catch (error) {
    console.error(`Error fetching agent ${agentId} details from v2 endpoint:`, error);

    // Check if it's a 503 Service Unavailable or other server error
    if (
      error.response &&
      (error.response.status === 503 ||
        error.response.status === 501 ||
        error.response.status === 404)
    ) {
      console.log(
        `V2 endpoint unavailable (${error.response.status}), falling back to legacy endpoint`
      );

      try {
        // Fall back to legacy endpoint
        const legacyUrl = API_ROUTES.AGENT_DETAILS_LEGACY.replace(':agentId', agentId);
        response = await api.get(legacyUrl);
        console.log('Successfully fetched agent details from legacy endpoint');
        usedLegacyEndpoint = true;
      } catch (legacyError) {
        console.error(`Error fetching agent ${agentId} details from legacy endpoint:`, legacyError);
        throw legacyError;
      }
    } else {
      // For other errors, just throw
      throw error;
    }
  }

  return {
    success: true,
    data: response.data || response,
    requestId: response.headers?.['x-request-id'],
    usedLegacyEndpoint,
  };
};

/**
 * Create a new agent
 * @param {Object} agentData - Agent data
 * @returns {Promise<Object>} - Created agent
 */
export const createAgent = async agentData => {
  try {
    const response = await api.post(API_ROUTES.AGENT_CREATE, agentData);

    return {
      success: true,
      data: response.data || response,
      requestId: response.headers?.['x-request-id'],
    };
  } catch (error) {
    console.error('Error creating agent:', error);
    throw error;
  }
};

/**
 * Update an existing agent
 * @param {string} agentId - ID of the agent to update
 * @param {Object} agentData - Updated agent data
 * @returns {Promise<Object>} - Updated agent
 */
export const updateAgent = async (agentId, agentData) => {
  try {
    const url = API_ROUTES.AGENT_UPDATE.replace(':agentId', agentId);
    const response = await api.put(url, agentData);

    return {
      success: true,
      data: response.data || response,
      requestId: response.headers?.['x-request-id'],
    };
  } catch (error) {
    console.error(`Error updating agent ${agentId}:`, error);
    throw error;
  }
};

/**
 * Delete an agent
 * @param {string} agentId - ID of the agent to delete
 * @returns {Promise<Object>} - Operation result
 */
export const deleteAgent = async agentId => {
  try {
    const url = API_ROUTES.AGENT_DELETE.replace(':agentId', agentId);
    const response = await api.delete(url);

    return {
      success: true,
      data: response.data || response,
      requestId: response.headers?.['x-request-id'],
    };
  } catch (error) {
    console.error(`Error deleting agent ${agentId}:`, error);
    throw error;
  }
};

/**
 * Get agent statistics
 * @param {string} tenantId - Optional tenant ID to filter by (admin only)
 * @returns {Promise<Object>} - Agent statistics
 */
export const getAgentStats = async (tenantId = null) => {
  const params = new URLSearchParams();
  if (tenantId) {
    params.append('tenantId', tenantId); // Use camelCase for v2 API
  }

  let response;
  let usedLegacyEndpoint = false;

  try {
    // Try v2 endpoint first
    console.log(`Attempting to fetch agent stats from v2 endpoint: ${API_ROUTES.AGENT_STATS}`);
    response = await api.get(`${API_ROUTES.AGENT_STATS}?${params}`);
    console.log('Successfully fetched agent stats from v2 endpoint');
  } catch (error) {
    console.error(`Error fetching agent stats from v2 endpoint ${API_ROUTES.AGENT_STATS}:`, error);

    // Check if it's a 503 Service Unavailable or other server error
    if (
      error.response &&
      (error.response.status === 503 ||
        error.response.status === 501 ||
        error.response.status === 404)
    ) {
      console.log(
        `V2 endpoint unavailable (${error.response.status}), falling back to legacy endpoint: ${API_ROUTES.AGENT_STATS_LEGACY}`
      );

      try {
        // Fall back to legacy endpoint
        // For legacy endpoint, we might need to adjust the parameter name
        const legacyParams = new URLSearchParams();
        if (tenantId) {
          legacyParams.append('tenant', tenantId); // Legacy might use 'tenant' instead of 'tenantId'
        }

        response = await api.get(`${API_ROUTES.AGENT_STATS_LEGACY}?${legacyParams}`);
        console.log('Successfully fetched agent stats from legacy endpoint');
        usedLegacyEndpoint = true;
      } catch (legacyError) {
        console.error(
          `Error fetching agent stats from legacy endpoint ${API_ROUTES.AGENT_STATS_LEGACY}:`,
          legacyError
        );
        throw legacyError;
      }
    } else {
      // For other errors, just throw
      throw error;
    }
  }

  return {
    success: true,
    data: response,
    requestId: response.headers?.['x-request-id'],
    usedLegacyEndpoint,
  };
};

/**
 * Get audit logs for an agent
 * @param {string} agentId - ID of the agent
 * @param {Object} params - Query parameters
 * @returns {Promise<Object>} - Audit logs
 */
export const getAgentAuditLogs = async (agentId, params = {}) => {
  const queryParams = new URLSearchParams(
    Object.entries(params)
      .filter(([_, value]) => value !== null && value !== undefined && value !== '')
      .map(([key, value]) => [key, String(value)])
  );

  let response;
  let usedLegacyEndpoint = false;

  try {
    // Try v2 endpoint first
    const url = API_ROUTES.AGENT_AUDIT.replace(':agentId', agentId);
    console.log(`Attempting to fetch audit logs from v2 endpoint: ${url}`);
    response = await api.get(`${url}?${queryParams}`);
    console.log('Successfully fetched audit logs from v2 endpoint');
  } catch (error) {
    console.error(`Error fetching audit logs for agent ${agentId} from v2 endpoint:`, error);

    // Check if it's a 503 Service Unavailable or other server error
    if (
      error.response &&
      (error.response.status === 503 ||
        error.response.status === 501 ||
        error.response.status === 404)
    ) {
      console.log(
        `V2 endpoint unavailable (${error.response.status}), falling back to legacy endpoint`
      );

      try {
        // Fall back to legacy endpoint
        const legacyUrl = API_ROUTES.AGENT_AUDIT_LEGACY.replace(':agentId', agentId);
        response = await api.get(`${legacyUrl}?${queryParams}`);
        console.log('Successfully fetched audit logs from legacy endpoint');
        usedLegacyEndpoint = true;
      } catch (legacyError) {
        console.error(
          `Error fetching audit logs for agent ${agentId} from legacy endpoint:`,
          legacyError
        );
        throw legacyError;
      }
    } else {
      // For other errors, just throw
      throw error;
    }
  }

  return {
    success: true,
    data: response.data || response,
    requestId: response.headers?.['x-request-id'],
    usedLegacyEndpoint,
  };
};

/**
 * Restart an agent
 * @param {string} agentId - ID of the agent to restart
 * @returns {Promise<Object>} - Operation result
 */
export const restartAgent = async agentId => {
  let response;
  let usedLegacyEndpoint = false;

  try {
    // Try v2 endpoint first
    const url = API_ROUTES.AGENT_RESTART.replace(':agentId', agentId);
    console.log(`Attempting to restart agent using v2 endpoint: ${url}`);
    response = await api.post(url);
    console.log('Successfully restarted agent using v2 endpoint');
  } catch (error) {
    console.error(`Error restarting agent ${agentId} using v2 endpoint:`, error);

    // Check if it's a 503 Service Unavailable or other server error
    if (
      error.response &&
      (error.response.status === 503 ||
        error.response.status === 501 ||
        error.response.status === 404)
    ) {
      console.log(
        `V2 endpoint unavailable (${error.response.status}), falling back to legacy endpoint`
      );

      try {
        // Fall back to legacy endpoint if it exists
        // Note: If there's no direct legacy equivalent, we might need to implement a different approach
        if (API_ROUTES.AGENT_RESTART_LEGACY) {
          const legacyUrl = API_ROUTES.AGENT_RESTART_LEGACY.replace(':agentId', agentId);
          response = await api.post(legacyUrl);
          console.log('Successfully restarted agent using legacy endpoint');
          usedLegacyEndpoint = true;
        } else {
          console.warn('No legacy endpoint available for agent restart, cannot fall back');
          throw error;
        }
      } catch (legacyError) {
        console.error(`Error restarting agent ${agentId} using legacy endpoint:`, legacyError);
        throw legacyError;
      }
    } else {
      // For other errors, just throw
      throw error;
    }
  }

  return {
    success: true,
    data: response,
    requestId: response.headers?.['x-request-id'],
    usedLegacyEndpoint,
  };
};

/**
 * Update agent configuration
 * @param {string} agentId - ID of the agent to update
 * @param {Object} config - New configuration
 * @returns {Promise<Object>} - Operation result
 */
export const updateAgentConfig = async (agentId, config) => {
  let response;
  let usedLegacyEndpoint = false;

  try {
    // Try v2 endpoint first
    const url = API_ROUTES.AGENT_CONFIG.replace(':agentId', agentId);
    console.log(`Attempting to update agent config using v2 endpoint: ${url}`);
    response = await api.put(url, config);
    console.log('Successfully updated agent config using v2 endpoint');
  } catch (error) {
    console.error(`Error updating agent ${agentId} configuration using v2 endpoint:`, error);

    // Check if it's a 503 Service Unavailable or other server error
    if (
      error.response &&
      (error.response.status === 503 ||
        error.response.status === 501 ||
        error.response.status === 404)
    ) {
      console.log(
        `V2 endpoint unavailable (${error.response.status}), falling back to legacy endpoint`
      );

      try {
        // Fall back to legacy endpoint if it exists
        // Note: If there's no direct legacy equivalent, we might need to implement a different approach
        if (API_ROUTES.AGENT_CONFIG_LEGACY) {
          const legacyUrl = API_ROUTES.AGENT_CONFIG_LEGACY.replace(':agentId', agentId);
          response = await api.put(legacyUrl, config);
          console.log('Successfully updated agent config using legacy endpoint');
          usedLegacyEndpoint = true;
        } else {
          console.warn('No legacy endpoint available for agent config update, cannot fall back');
          throw error;
        }
      } catch (legacyError) {
        console.error(
          `Error updating agent ${agentId} configuration using legacy endpoint:`,
          legacyError
        );
        throw legacyError;
      }
    } else {
      // For other errors, just throw
      throw error;
    }
  }

  return {
    success: true,
    data: response,
    requestId: response.headers?.['x-request-id'],
    usedLegacyEndpoint,
  };
};

export default {
  listAgents,
  getAgentDetails,
  createAgent,
  updateAgent,
  deleteAgent,
  getAgentStats,
  getAgentAuditLogs,
  restartAgent,
  updateAgentConfig,
};
